import { useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router-dom';
import { TEAM_LABELS } from '@kalkuel-inbox/shared';
import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  Search,
  MessageSquare,
  MessageCircle,
  Mail,
  Plus,
  Inbox as InboxIcon,
  Trash2,
  Star,
  Instagram,
  Facebook,
} from 'lucide-react';
import { api } from '../lib/api';
import { wsClient, useWsEvent } from '../lib/ws';
import { formatRelative, messagePreview } from '../lib/format';
import { FilterChips, Button } from '../components/ui';
import { NewMessageModal } from '../components/NewMessageModal';
import { ConversationActionsMenu } from '../components/ConversationActionsMenu';
import { OPEN_NEW_MESSAGE_EVENT } from '../components/CommandPalette';
import type {
  ConversationListItem,
  ConversationTag,
  Channel,
  ChannelAccountInfo,
  Tag,
} from '../types';

export function InboxPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Schlanke Filter: nur Postfach/Nummer, „Ungelesen" und „Erledigte anzeigen" + Suche.
  const [channelAccountId, setChannelAccountId] = useState<'ALL' | string>('ALL');
  const [unread, setUnread] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [important, setImportant] = useState(false);
  const [trashed, setTrashed] = useState(false);
  const [tagId, setTagId] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [newMessageOpen, setNewMessageOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setQDebounced(q), 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    wsClient.subscribe('all');
    return () => wsClient.unsubscribe('all');
  }, []);

  // Command-Palette kann „Neue Nachricht" über ein globales Event öffnen.
  useEffect(() => {
    const open = () => setNewMessageOpen(true);
    window.addEventListener(OPEN_NEW_MESSAGE_EVENT, open);
    return () => window.removeEventListener(OPEN_NEW_MESSAGE_EVENT, open);
  }, []);

  // Postfächer (ChannelAccounts) = die „Nummern". Je ein Chip pro Postfach + „Alle".
  const { data: accountsData } = useQuery({
    queryKey: ['channel-accounts'],
    queryFn: () => api.get<{ items: ChannelAccountInfo[] }>('/api/channel-accounts'),
  });
  const accounts = accountsData?.items ?? [];
  // Nur AKTIVE Postfächer als Filter anbieten. Stillgelegte Konten (z.B. das alte
  // „vertrieb@kalkuel.de" oder der abgeschaffte Webchat) sind isActive=false und
  // sollen nicht mehr als Filter-Chip erscheinen; ihre Altkonversationen bleiben
  // unter „Alle" weiterhin sichtbar.
  const postfachOptions = useMemo(
    () => [
      { value: 'ALL' as const, label: 'Alle' },
      ...accounts
        .filter((a) => a.isActive !== false)
        .map((a) => ({ value: a.id, label: a.displayName })),
    ],
    [accounts],
  );

  // Tag-Filter nur sinnvoll, wenn genau ein Postfach gewählt ist (Tags sind je Postfach skopiert).
  const tagFilterAccountId = channelAccountId !== 'ALL' ? channelAccountId : null;
  const { data: tagsData } = useQuery({
    queryKey: ['channel-account-tags', tagFilterAccountId],
    queryFn: () => api.get<{ items: Tag[] }>(`/api/channel-accounts/${tagFilterAccountId}/tags`),
    enabled: Boolean(tagFilterAccountId),
  });
  const accountTags = tagsData?.items ?? [];

  // Beim Wechsel des Postfachs den (postfach-skopierten) Tag-Filter zurücksetzen.
  useEffect(() => {
    setTagId(null);
  }, [channelAccountId]);

  // ---- Konversationsliste (Infinite-Scroll über nextCursor) --------------
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: [
      'conversations',
      { channelAccountId, unread, showClosed, important, trashed, tagId, q: qDebounced },
    ] as const,
    initialPageParam: undefined as string | undefined,
    // Beim Filterwechsel (anderer queryKey) die vorherige Liste als Platzhalter behalten,
    // bis die neuen Daten da sind → kein Lade-Flash / Leersprung der Liste.
    placeholderData: keepPreviousData,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (channelAccountId !== 'ALL') params.set('channelAccountId', channelAccountId);
      if (unread) params.set('unread', '1');
      if (showClosed) params.set('includeClosed', '1');
      if (important) params.set('important', '1');
      if (trashed) params.set('trashed', '1');
      if (tagId) params.set('tagId', tagId);
      if (qDebounced) params.set('q', qDebounced);
      if (pageParam) params.set('cursor', pageParam);
      return api.get<{ items: ConversationListItem[]; nextCursor: string | null }>(
        `/api/conversations?${params.toString()}`,
      );
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  // Realtime: nicht bei JEDEM Event refetchen (Invalidation-Storm). Events über ein
  // kurzes Fenster bündeln und dann EINMAL invalidieren — bei 20 Nachrichten/Min wird
  // so aus 20 Refetches einer. (Gezielte setQueryData-Patches wären noch sparsamer,
  // sind mit Infinite-Pages + Filtervarianten aber fehleranfällig.)
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useWsEvent((event) => {
    if (
      event.type !== 'conversation.created' &&
      event.type !== 'conversation.updated' &&
      event.type !== 'message.created'
    ) {
      return;
    }
    if (refetchTimer.current) return; // bereits ein Refetch eingeplant
    refetchTimer.current = setTimeout(() => {
      refetchTimer.current = null;
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    }, 400);
  });
  useEffect(() => () => {
    if (refetchTimer.current) clearTimeout(refetchTimer.current);
  }, []);

  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
  const showPostfachChips = accounts.length > 1;

  // Auto-Nachladen, wenn der Sentinel am Listenende sichtbar wird.
  const sentinelRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
      },
      { root: el.closest('ul'), rootMargin: '200px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
    // items.length bewusst NICHT als Dep: der Observer muss nicht bei jeder neuen
    // Listenzeile neu aufgebaut werden. fetchNextPage ist über Renders stabil
    // (TanStack Query v5), Sentinel-Ref + Scroll-Root (ul) sind konstant.
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // WARNUNG: min-h-0, grid-rows-[minmax(0,1fr)], overflow-hidden + section min-h-0
  // NICHT entfernen. Ohne row-clamp wächst die Grid-Row mit dem Inhalt der linken
  // Konversationsliste, dadurch wird die ganze Page höher als der Viewport und der
  // Composer der eingebetteten ConversationPage landet außerhalb des Sichtbereichs.
  // Siehe inbox/entscheidungen.md ("2026-06-15 — Compose-Box-Sichtbarkeit").
  return (
    <div className="grid h-full min-h-0 grid-cols-[380px_1fr] grid-rows-[minmax(0,1fr)] overflow-hidden bg-kalkuel-surface">
      <section className="flex h-full min-h-0 flex-col border-r border-kalkuel-border bg-white">
        <header className="space-y-3 border-b border-kalkuel-border p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h1 className="kalkuel-page-title">Posteingang</h1>
              <p className="mt-0.5 text-xs text-kalkuel-muted">
                {items.length} {items.length === 1 ? 'Konversation' : 'Konversationen'}
              </p>
            </div>
            <Button size="sm" onClick={() => setNewMessageOpen(true)}>
              <Plus size={13} />
              Neue Nachricht
            </Button>
          </div>

          <div className="relative">
            <Search
              size={14}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-kalkuel-muted"
            />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Suche Kontakt oder Nachricht…"
              aria-label="Konversationen durchsuchen"
              className="h-9 w-full rounded-pill border border-kalkuel-border bg-kalkuel-surface pl-9 pr-3 text-sm transition-shadow placeholder:text-kalkuel-muted focus:border-kalkuel-accent focus:bg-white focus:outline-none focus:shadow-focus-accent"
            />
          </div>

          {/* Eine ruhige Filterzeile: Nummern · Ungelesen · Wichtig · Erledigte · Papierkorb */}
          <div className="flex flex-wrap items-center gap-1.5">
            {showPostfachChips && (
              <>
                <FilterChips
                  value={channelAccountId}
                  options={postfachOptions}
                  onChange={setChannelAccountId}
                  size="sm"
                />
                <span aria-hidden className="mx-0.5 h-4 w-px bg-kalkuel-border" />
              </>
            )}
            <ToggleChip active={unread} onClick={() => setUnread((v) => !v)} disabled={trashed}>
              Ungelesen
            </ToggleChip>
            <ToggleChip active={important} onClick={() => setImportant((v) => !v)} disabled={trashed}>
              Wichtig
            </ToggleChip>
            <ToggleChip
              active={showClosed}
              onClick={() => setShowClosed((v) => !v)}
              disabled={trashed}
            >
              Erledigte anzeigen
            </ToggleChip>
            <span aria-hidden className="mx-0.5 h-4 w-px bg-kalkuel-border" />
            <ToggleChip
              active={trashed}
              onClick={() => {
                setTrashed((v) => {
                  // Beim Wechsel in den Papierkorb die (dann gesperrten) Filter zurücksetzen,
                  // damit kein stale important/unread/showClosed das Trash-Ergebnis verdeckt.
                  if (!v) {
                    setUnread(false);
                    setImportant(false);
                    setShowClosed(false);
                  }
                  return !v;
                });
              }}
            >
              <Trash2 size={11} className="mr-1 inline" />
              Papierkorb
            </ToggleChip>
          </div>

          {/* Tag-Filter: nur bei genau einem gewählten Postfach (Tags sind postfach-skopiert). */}
          {tagFilterAccountId && accountTags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-medium uppercase tracking-wider text-kalkuel-muted">
                Tags
              </span>
              {accountTags.map((t) => {
                const active = tagId === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setTagId(active ? null : t.id)}
                    style={
                      active
                        ? { backgroundColor: t.color, color: '#fff', borderColor: t.color }
                        : { color: t.color, borderColor: t.color + '55' }
                    }
                    className="inline-flex h-6 items-center gap-1 rounded-pill border px-2 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-kalkuel-accent/40"
                  >
                    {!active && (
                      <span
                        className="size-2 rounded-full"
                        style={{ backgroundColor: t.color }}
                        aria-hidden
                      />
                    )}
                    {t.name}
                  </button>
                );
              })}
            </div>
          )}
        </header>

        <ul className="flex-1 divide-y divide-kalkuel-border/70 overflow-y-auto">
          {isLoading && <ListSkeleton />}
          {!isLoading && items.length === 0 && (
            <EmptyState unread={unread} important={important} trashed={trashed} q={qDebounced} />
          )}
          {!isLoading &&
            items.map((c) => {
              const active = conversationId === c.id;
              const isUnread = c.unread === true && !active;
              return (
                <li key={c.id} className="group relative">
                  <button
                    onClick={() => navigate(`/inbox/${c.id}`)}
                    aria-current={active ? 'true' : undefined}
                    className={[
                      'flex w-full flex-col items-start gap-1 border-l-2 px-4 py-3 pr-10 text-left transition-colors duration-150',
                      active
                        ? 'border-kalkuel-accent-strong bg-kalkuel-accent-soft'
                        : isUnread
                          ? 'border-kalkuel-accent2 bg-kalkuel-accent-soft/40 hover:bg-kalkuel-accent-soft/70'
                          : 'border-transparent hover:bg-kalkuel-surface-2',
                    ].join(' ')}
                  >
                    <div className="flex w-full items-center gap-2">
                      {isUnread && <span className="sr-only">Ungelesen. </span>}
                      {/* Ungelesen-Punkt: feste Breite (auch leer) → keine horizontale Verschiebung. */}
                      <span className="flex w-2 shrink-0 justify-center" aria-hidden>
                        {isUnread && (
                          <span className="size-2 rounded-full bg-kalkuel-accent2" />
                        )}
                      </span>
                      <ChannelIcon channel={c.channel} />
                      <span
                        className={[
                          'min-w-0 flex-1 truncate text-sm',
                          active
                            ? 'font-semibold text-kalkuel-ink'
                            : isUnread
                              ? 'font-semibold text-kalkuel-primary'
                              : 'font-medium text-kalkuel-primary/90',
                        ].join(' ')}
                      >
                        {c.contact.displayName}
                      </span>
                      {c.isImportant && (
                        <Star
                          size={13}
                          aria-label="wichtig"
                          className="shrink-0 fill-amber-400 text-amber-400"
                        />
                      )}
                      <span
                        className={[
                          'shrink-0 text-[11px] tabular-nums',
                          isUnread ? 'font-medium text-kalkuel-accent2' : 'text-kalkuel-muted',
                        ].join(' ')}
                      >
                        {formatRelative(c.lastMsgAt)}
                      </span>
                    </div>
                    <span
                      className={[
                        'line-clamp-1 w-full pl-6 text-xs',
                        isUnread ? 'font-medium text-kalkuel-primary/80' : 'text-kalkuel-muted',
                      ].join(' ')}
                    >
                      {messagePreview(c.messages[0])}
                    </span>
                    <span className="flex w-full items-center gap-1.5 overflow-hidden pl-6">
                      <span className="shrink-0 text-[10px] font-medium uppercase tracking-wider text-kalkuel-accent">
                        {TEAM_LABELS[c.assignedTeam] ?? c.assignedTeam}
                      </span>
                      {showPostfachChips && c.channelAccount && (
                        <span className="max-w-[120px] shrink-0 truncate rounded-pill bg-kalkuel-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-kalkuel-muted">
                          {c.channelAccount.displayName}
                        </span>
                      )}
                      <TagPills tags={c.tags} />
                    </span>
                  </button>
                  {/* Aktionsmenü: absolut über dem Navigations-Button, eigenes stopPropagation.
                      Reservierter Platz rechts (pr-10) → kein Layout-Shift beim Einblenden. */}
                  <div className="absolute right-2 top-2.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 data-[open=true]:opacity-100">
                    <ConversationActionsMenu
                      conversation={{
                        id: c.id,
                        status: c.status,
                        isImportant: c.isImportant,
                        deletedAt: c.deletedAt,
                        channelAccount: { id: c.channelAccount?.id ?? '' },
                        tags: c.tags,
                      }}
                      variant="compact"
                    />
                  </div>
                </li>
              );
            })}
          {/* Sentinel + Lade-Fuß für Infinite-Scroll */}
          {!isLoading && items.length > 0 && (
            <li ref={sentinelRef} className="px-4 py-3 text-center text-xs text-kalkuel-muted">
              {isFetchingNextPage ? 'Lädt weitere…' : hasNextPage ? '' : ''}
            </li>
          )}
        </ul>
      </section>
      <Outlet />
      <NewMessageModal open={newMessageOpen} onClose={() => setNewMessageOpen(false)} />
    </div>
  );
}

/** Toggle-Chip im FilterChips-Look — für „Ungelesen" / „Wichtig" / „Erledigte" / „Papierkorb". */
function ToggleChip({
  active,
  onClick,
  children,
  disabled = false,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={[
        'inline-flex h-6 items-center rounded-pill px-2 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-kalkuel-accent/40',
        'disabled:cursor-not-allowed disabled:opacity-40',
        active
          ? 'bg-kalkuel-primary text-white'
          : 'border border-kalkuel-border bg-white text-kalkuel-primary hover:border-kalkuel-primary',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function EmptyState({
  unread,
  important,
  trashed,
  q,
}: {
  unread: boolean;
  important: boolean;
  trashed: boolean;
  q: string;
}) {
  const text = q
    ? 'Keine Treffer für deine Suche.'
    : trashed
      ? 'Der Papierkorb ist leer.'
      : important
        ? 'Keine als wichtig markierten Konversationen.'
        : unread
          ? 'Alles gelesen — keine ungelesenen Konversationen.'
          : 'Keine Konversationen.';
  return (
    <li className="flex flex-col items-center gap-2 px-4 py-12 text-center">
      <InboxIcon size={28} className="text-kalkuel-border" />
      <span className="text-sm text-kalkuel-muted">{text}</span>
    </li>
  );
}

function ListSkeleton() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="flex flex-col gap-2 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="h-3.5 w-32 animate-pulse rounded bg-kalkuel-border/60" />
            <div className="h-3 w-10 animate-pulse rounded bg-kalkuel-border/40" />
          </div>
          <div className="h-3 w-48 animate-pulse rounded bg-kalkuel-border/40" />
        </li>
      ))}
    </>
  );
}

/**
 * Kanal-Icon — resilient gegen unbekannte/archivierte Kanäle. `channel` wird als
 * String behandelt (Altdaten können Werte außerhalb des aktuellen `Channel`-Unions
 * liefern, z.B. 'WEBCHAT'); jeder unbekannte Wert fällt auf ein neutrales
 * Default-Icon zurück statt zu fehlen.
 */
function ChannelIcon({ channel }: { channel: Channel | string }) {
  switch (channel) {
    case 'WHATSAPP':
      return <MessageSquare size={14} aria-label="WhatsApp" className="shrink-0 text-kalkuel-accent2" />;
    case 'EMAIL':
      return <Mail size={14} aria-label="E-Mail" className="shrink-0 text-kalkuel-primary" />;
    case 'INSTAGRAM':
      return <Instagram size={14} aria-label="Instagram" className="shrink-0 text-kalkuel-muted" />;
    case 'FACEBOOK':
      return <Facebook size={14} aria-label="Facebook" className="shrink-0 text-kalkuel-muted" />;
    case 'WEBCHAT':
      return <MessageCircle size={14} aria-label="Webchat" className="shrink-0 text-kalkuel-muted" />;
    default:
      // Unbekannter/archivierter Kanal → neutrales Default-Icon (kein Crash, keine Leerstelle).
      return <MessageCircle size={14} aria-label="Kanal" className="shrink-0 text-kalkuel-muted" />;
  }
}

/**
 * Tag-Pills für eine Listenzeile: einheitliche Höhe, abgeschnitten, mit
 * „+N"-Überlauf statt Umbruch-Chaos. Zeigt maximal `max` Pills, der Rest wird zu
 * einer dezenten „+N"-Pille zusammengefasst (Titel listet die restlichen Namen).
 */
function TagPills({ tags, max = 2 }: { tags: ConversationTag[]; max?: number }) {
  if (tags.length === 0) return null;
  const visible = tags.slice(0, max);
  const overflow = tags.slice(max);
  return (
    <span className="flex min-w-0 items-center gap-1 overflow-hidden">
      {visible.map((t) => (
        <span
          key={t.tag.id}
          style={{ backgroundColor: t.tag.color + '22', color: t.tag.color }}
          className="inline-flex h-[18px] max-w-[110px] shrink-0 items-center truncate rounded-pill px-1.5 text-[10px] font-medium leading-none"
        >
          {t.tag.name}
        </span>
      ))}
      {overflow.length > 0 && (
        <span
          title={overflow.map((t) => t.tag.name).join(', ')}
          className="inline-flex h-[18px] shrink-0 items-center rounded-pill bg-kalkuel-surface-2 px-1.5 text-[10px] font-medium leading-none text-kalkuel-muted"
        >
          +{overflow.length}
        </span>
      )}
    </span>
  );
}
